import { createSelector } from 'reselect';
import {
  AGREEMENT_TYPES,
  DELIVERY_METHODS,
  MIN_REDEEMABLE_THRESHOLD_DOLLAR_PERCENTAGE,
  UNIT,
} from 'src/settings/settings';
import { getDeliveryMethod } from '../sellDeliveryMethod/sellDeliveryMethod.selectors';
import {
  DELIVERY_MAIL_CHECK_QCD,
  DELIVERY_MAIL_CHECK_TO_CHARITY,
  MAX_CHARITY_DONATION_MAIL_TO_CHARITY,
  MAX_CHARITY_DONATION_MAIL_TO_ME,
  MIN_CHARITY_DONATION,
} from '../../../settings/settings';
import {
  getUserFundsRaw,
  getUserRetirementFunds,
} from '../funds/UserFunds/userFunds.selectors';
import {
  getUserBPID,
  isDateOfBirthAvailable,
} from '../user/PersonalInformation/personalInformation.selectors';
import {
  getIsRMDEligible,
  getRMDAvailableBalance,
  getSystematicLE,
  getTotalPendingRMDAmount,
  getTotalSystematicRMDAmount,
} from '../RMD/RMDSetting.selectors';
import { updateQCDFundNotesForFund } from '../../../utils/funds/qcd/qcd-fund-notes-utility';
import {
  getMultipleAddressFlag,
  getSellPendingTransactionsTotal,
} from '../sellTransaction/sellTransaction.selectors';
import {
  getHasUncollectedShares,
  setFundPendingTransaction,
  setQCDFundLimitMessage,
} from '../../../utils/funds/funds-utility';
import { getPersonalInformationData } from '../user/PersonalInformation/personalInformation.selectors.ts';
import { add, get, subtract } from 'lodash';
import { coalesce } from '../../../utils/utils';
import { getRedeemThresholds } from '../redeemThresholds/redeemThresholds.selectors';
import {
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
  getSellPendingTransactions,
} from '../pendingTransactions/pendingTransactions.selectors';

export const getCharities = (state) => state.qcd.charities || {};

export const getQCDFunds = (state) => state.qcd.iraQCDFunds.funds || undefined;

export const getCheckbox = (state) => state.qcd.checkbox || false;

export const getIsReduceAutoRMD = (state) => state.qcd.reduceAutoRMD || false;

export const getQCDSelectedFunds = (state) => state.qcd.qcdSelectedFunds || {};

export const getQCDLoading = (state) => state.qcd.QCDLoading;

export const getQCDFailure = (state) => state.qcd.QCDFailure;

export const getQCDSuccess = (state) => state.qcd.QCDSuccess;

export const getQCDTransaction = (state) =>
  state.qcd.QCDTransactionSuccess.transactions || [];

export const getEligibleQcdTradeDates = (state) =>
  state.qcd.qcdTradeDates?.estimatedTradeDate || {};

// To pull in qcdSubmittedToday flag in entity/details call
export const getQCDSubmittedToday = createSelector(
  [getPersonalInformationData],
  (personalInformation) => {
    return get(personalInformation, 'qcdSubmittedToday');
  }
);

function confirmDeliveryMethod(method) {
  return createSelector(getDeliveryMethod, (deliveryMethod) => {
    return deliveryMethod === method;
  });
}

export const getIsMailACheck = confirmDeliveryMethod(
  DELIVERY_METHODS.MAIL_A_CHECK_QCD
);

export const getIsMailACheckToCharity = confirmDeliveryMethod(
  DELIVERY_METHODS.MAIL_A_CHECK_CHARITY
);

export const getCharitiesLength = createSelector(getCharities, (charities) => {
  return Object.values(charities).length;
});

export const getIsStep1NextButtonDisabled = createSelector(
  [getIsMailACheck, getIsMailACheckToCharity, getCharitiesLength],
  (isMailACheck, isMailACheckToCharity, charitiesLength) => {
    // TODO - adjust length for when choosing check to address
    return !(isMailACheckToCharity || isMailACheck) || charitiesLength < 1;
  }
);

export const getCharitiesArray = createSelector(
  getCharities,
  getDeliveryMethod,
  (charities, deliveryMethod) => {
    const charityList = Object.values(charities);
    return charityList.filter(
      (charity) => charity.deliveryMethod === deliveryMethod
    );
  }
);

export const getQCD = (state) => state.qcd;

export const getCharitiesTotalGrossAmount = createSelector(
  getCharitiesArray,
  (charities) => {
    let total = 0;
    charities.forEach((charity) => {
      total =
        Math.floor((total + (charity?.grossAmountDollars || 0)) * 1000) / 1000;
    });
    return total;
  }
);

export const getCharitiesEveryCharityGrossAmountAboveOne = createSelector(
  getCharitiesArray,
  getDeliveryMethod,
  getCharitiesTotalGrossAmount,
  (charities, deliveryMethod, total) => {
    if (deliveryMethod === DELIVERY_MAIL_CHECK_TO_CHARITY) {
      return (
        charities.every((charity) => {
          return (
            charity?.grossAmountDollars >= MIN_CHARITY_DONATION &&
            charity?.grossAmountDollars <= MAX_CHARITY_DONATION_MAIL_TO_CHARITY
          );
        }) && total <= MAX_CHARITY_DONATION_MAIL_TO_CHARITY
      );
    } else if (deliveryMethod === DELIVERY_MAIL_CHECK_QCD) {
      return (
        charities.every((charity) => {
          return (
            charity?.grossAmountDollars >= MIN_CHARITY_DONATION &&
            charity?.grossAmountDollars <= MAX_CHARITY_DONATION_MAIL_TO_ME
          );
        }) && total <= MAX_CHARITY_DONATION_MAIL_TO_ME
      );
    }
  }
);

export const isRestrictedAddressChange = createSelector(
  getUserRetirementFunds,
  getDeliveryMethod,
  (funds, deliveryMethod) => {
    let allFunds = [];
    Object.keys(funds).forEach((key) => {
      allFunds = [...allFunds, ...funds[key]];
    });
    return (
      allFunds?.every((fund) => fund?.restrictedByAddressChange === true) &&
      deliveryMethod === 'CheckQCD'
    );
  }
);

export const getAllQCDEligibleFunds = createSelector(
  getQCDFunds,
  getUserFundsRaw,
  (agreementIds, userFunds) => {
    if (
      userFunds?.[0].nonRetirementAgreements !== undefined &&
      agreementIds !== undefined
    ) {
      const nonRetirementFunds =
        Object.values(userFunds?.[0]?.nonRetirementAgreements) || [];
      const retirementFunds =
        Object.values(userFunds?.[0]?.retirementAgreements) || [];
      var newNonRetirementFunds = Array.prototype.concat.apply(
        [],
        nonRetirementFunds
      );
      var newRetirementFunds = Array.prototype.concat.apply(
        [],
        retirementFunds
      );
      const allFunds = newNonRetirementFunds.concat(newRetirementFunds);
      return allFunds.filter((agreement) => {
        return agreementIds.some((id) => id === agreement.agreementID);
      });
    } else {
      return [];
    }
  }
);

export const getEligibleQCDFundsList = createSelector(
  getQCDSelectedFunds,
  getAllQCDEligibleFunds,
  getIsRMDEligible,
  isDateOfBirthAvailable,
  getDeliveryMethod,
  getMultipleAddressFlag,
  getUserBPID,
  getCharitiesTotalGrossAmount,
  getRedeemThresholds,
  getSellPendingTransactions,
  getSellPendingTransactionsTotal,
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
  (
    selectedFunds,
    funds,
    isRMDEligible,
    dateOfBirthAvailable,
    deliveryMethod,
    isCheckWithMultipleAddress,
    BPID,
    totalGrossAmountCharity,
    redeemThresholds,
    sellPendingTransactions,
    sellPendingTransactionsTotal,
    pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
  ) => {
    let total = 0;
    if (selectedFunds !== undefined) {
      const selectorFunds = Object.values(selectedFunds).filter(
        (fund) => fund.selected
      );
      selectorFunds.forEach((charity) => {
        total = Math.floor((total + charity?.grossAmountDollars) * 1000) / 1000;
      });
    }
    return funds
      .filter((ownedFunds) =>
        ownedFunds.agreementParties.some(
          (party) => party.roleCode === 'OWNR' && party.businessPartyID === BPID
        )
      )
      .map((fund) => {
        fund.selected = selectedFunds[fund.agreementID]?.selected || false;
        fund.grossUnit = UNIT.DOLLARS;
        fund.isFundRMDEligible =
          !!isRMDEligible &&
          (fund.agreementType === AGREEMENT_TYPES.ROLLOVER_IRA ||
            fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA) &&
          !!dateOfBirthAvailable;
        fund.grossAmountDollars =
          selectedFunds[fund.agreementID]?.grossAmountDollars || 0;
        fund.notes = {};
        fund.grossAmount =
          selectedFunds[fund.agreementID]?.grossAmountDollars || 0;
        fund.hasUncollectedShares = getHasUncollectedShares(
          fund.balanceAsOf,
          fund.equityAsOf,
          fund.productType
        );
        const redeemableThresholdDollarPercentage = get(
          redeemThresholds,
          fund.trpCode,
          MIN_REDEEMABLE_THRESHOLD_DOLLAR_PERCENTAGE
        );
        fund.redeemableFundDollars =
          Math.floor(redeemableThresholdDollarPercentage * fund.equityAsOf) /
          100;
        setFundPendingTransaction(
          fund,
          pendingBuyExchangeSellTransactionRecordsByAgreementGrouped
        );
        setQCDFundLimitMessage(fund, total, totalGrossAmountCharity);
        updateQCDFundNotesForFund(
          deliveryMethod,
          fund,
          isCheckWithMultipleAddress
        );
        return fund || [];
      });
  }
);

export const getEligibleQCDFundsTotalAmount = createSelector(
  getEligibleQCDFundsList,
  (funds) => {
    let total = 0;
    if (funds !== undefined) {
      const selectorFunds = funds.filter((fund) => fund.selected);
      selectorFunds.forEach((charity) => {
        total = Math.floor((total + charity?.grossAmountDollars) * 1000) / 1000;
      });
    }
    return total;
  }
);

export const getCharityNames = createSelector(
  getCharitiesArray,
  (charities) => {
    return (
      charities.map((charity) => {
        return ' ' + charity.charityName;
      }) || []
    );
  }
);

export const getSelectedQCDFunds = createSelector(
  getEligibleQCDFundsList,
  (qcdFundsList) => {
    return qcdFundsList.filter((qcdFund) => {
      return qcdFund.selected;
    });
  }
);

export const getRothAndRolloverOnly = createSelector(
  getSelectedQCDFunds,
  (selectedFunds) => {
    const selectedFundsList = Object.values(selectedFunds);
    const filteredList = selectedFundsList.filter(
      (fund) =>
        fund.agreementType === 'ROTH_IRA' ||
        fund.agreementType === 'ROTH_ROLLOVER_IRA'
    );
    return selectedFundsList.length === filteredList.length;
  }
);

export const getRMDReductionAmount = createSelector(
  getSelectedQCDFunds,
  (selectedFunds) => {
    let totalReduction = 0;
    Object.values(selectedFunds)
      .filter(
        (fund) =>
          fund.agreementType !== 'ROTH_IRA' &&
          fund.agreementType !== 'ROTH_ROLLOVER_IRA'
      )
      .forEach((fund) => {
        totalReduction += fund.grossAmount;
      });
    return totalReduction;
  }
);

export const getSelectedFundsAddress = createSelector(
  getSelectedQCDFunds,
  (selectedFunds) => {
    return (
      selectedFunds.map((fund) => {
        return (
          fund.address.street1 +
          ' ' +
          fund.address.city +
          ' ' +
          fund.address.state +
          ' ' +
          fund.address.zip +
          ' ' +
          fund.address.zipCodeExt
        );
      }) || []
    );
  }
);

export const getSingleAddressSelectedFunds = createSelector(
  getSelectedFundsAddress,
  (selectedFundsAddress) => {
    const filteredFunds = selectedFundsAddress.filter(
      (fund, idx) => selectedFundsAddress.indexOf(fund) === idx
    );
    return filteredFunds.length === 1;
  }
);

export const getSelectedQCDFundsHaveRedemption = createSelector(
  getSelectedQCDFunds,
  (qcdFundsList) => {
    return qcdFundsList.every((qcdFund) => {
      return qcdFund.grossAmountDollars >= 1;
    });
  }
);

export const getSelectedQCDFundsValid = createSelector(
  getSelectedQCDFunds,
  (qcdFundsList) => {
    return qcdFundsList.some((qcdFund) => {
      return !!qcdFund.QCDgrossAmountError;
    });
  }
);

export const getSubmissionPayload = createSelector(
  getSelectedQCDFunds,
  getCharitiesArray,
  getRMDReductionAmount,
  getIsMailACheck,
  getCharitiesTotalGrossAmount,
  getIsReduceAutoRMD,
  (
    selectedFunds,
    selectedCharities,
    rmdAmount,
    isMailACheck,
    totalAmount,
    reduceRMD
  ) => {
    return {
      '@Type': 'TransactionQualifiedCharityDistributionRequest',
      distribution: {
        distributionMethod: isMailACheck
          ? 'CHECK_TO_CLIENT'
          : 'CHECK_TO_CHARITY',
        totalDistributionAmount: totalAmount,
      },
      sourceAgreements: selectedFunds.map((fund) => {
        return {
          agreementID: fund.agreementID,
          distributionAmount: fund.grossAmountDollars,
        };
      }),
      charities: selectedCharities.map((charity) => {
        return {
          charityName: charity.charityName,
          attention: charity.attention,
          address: {
            address1: charity.addressLineOne,
            address2: charity.addressLineTwo,
            city: charity.city,
            state: charity.state,
            zipcode: charity.zip ? charity.zip + '-' + charity.zipExt : '',
          },
          charityAmount: charity.grossAmountDollars,
        };
      }),
      rmdInstruction: {
        reduceRMDAmountByQCD: reduceRMD ? 'Yes' : 'No',
      },
    };
  }
);

export const getRMDRemainingBalanceAmountQCD = createSelector(
  [
    getSelectedQCDFunds,
    getRMDAvailableBalance,
    getTotalPendingRMDAmount,
    getTotalSystematicRMDAmount,
    getSystematicLE,
  ],
  (
    QCDFundList,
    rmdAvailableBalance,
    totalPendingRMDAmount,
    totalSystematicRMDAmount,
    hasSystematicLE
  ) => {
    const funds = QCDFundList.filter((f) => f.isFundRMDEligible);
    function remainingRMDBalance(amount, fund) {
      return amount - coalesce(fund.grossAmountDollars, 0);
    }
    const totalPendingAndSystematicRMDAmount = add(
      totalPendingRMDAmount,
      totalSystematicRMDAmount
    );
    const totalRemainingRMD = subtract(
      rmdAvailableBalance,
      totalPendingAndSystematicRMDAmount
    );
    const original = coalesce(totalRemainingRMD, 0);
    const RMDBalanceAmount = funds.reduce(remainingRMDBalance, original);
    return RMDBalanceAmount > 0 && !hasSystematicLE ? RMDBalanceAmount : 0;
  }
);
